<script setup>
import {reactive, ref, watch} from "vue";
import AiChatLoader from "@/components/aiChat/AiChatLoader.vue";
import useAiChat from "@/composables/useAiChat";
import needsService from "@/services/needs.service";
import {stripPaste} from "@/helpers/string";
import {useRouter} from "vue2-helpers/vue-router";
import ConfirmModal from "@/components/ui/ConfirmModal.vue";

const router = useRouter();

const showModal = ref(false);
const content = ref(null);
const showResult = ref(false);
const showConfirmModal = ref(false);
const canHide = ref(false);

const roundFormat = {
    question: '',
    answer: '',
    output: null
};

const params = {
    url: '/chat-stream/get-needs-ai-response',
    roundFormat,
    content,
    addEvents: {
        'finished': finish
    }
}

const {
    question,
    rounds,
    loading,
    loaderMessage,
    clearStream,
    sendRequest,
    sanitizeAnswer,
    reset,
} = useAiChat(params);

const need = reactive({
    name: '',
    description: '',
    conversationId: 0
});

// ensure we always close stream when modal is hidden or component unmounts
watch(showModal, (value) => {
    if (!value) {
        clearStream();
    }
});

function finish(message) {
    const needData = JSON.parse(message);

    need.name = needData.name;
    need.description = needData.description;
    need.conversationId = needData.conversation_id;

    showResult.value = true;
}

function handleHide(event) {
    if (rounds.value.length && !canHide.value) {
        event.preventDefault();
        showConfirmModal.value = true;
    }
}

function closeConfirmed(option) {
    showConfirmModal.value = false;

    if (option === 'discard') {
        canHide.value = true;
        showModal.value = false;
    }
}

async function createNeed() {
    const {data} = await needsService.createNeed(need);

    router.push('/needs/' + data);
}

function open() {
    reset();
    showModal.value = true;
    canHide.value = false;
    showResult.value = false;
    need.name = '';
    need.description = '';
    need.conversationId = 0;
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" hide-footer hide-header modal-class="modal-ai-chat modal-ai-chat--need" @hide="handleHide">
        <!-- <a href="#" class="close">Back</a> -->
        <section class="section-ai-chat" ref="content">
            <button @click="showModal = false" type="button" aria-label="Close" class="close"></button>

            <div class="ai-chat__inner">
                <div class="ai-chat__content">
                    <div v-if="!rounds.length">
                        <h3 class="heading3">What need are you exploring?</h3>
                        <p>Describe a business challenge-Al and our ecosystem partners will help<br/> you find solutions.</p>

                        <div class="card--ai__search">
                        <span
                            role="textbox"
                            contenteditable
                            v-focus
                            placeholder="Describe your business challenge..."
                            class="card--ai__search__input"
                            @paste.prevent="stripPaste"
                            @input="question = $event.target.textContent"
                            @keyup.enter.prevent="sendRequest"
                        />

                            <button class="card--ai__search__button" @click="sendRequest"></button>
                        </div>
                    </div>

                    <template v-else >
                        <div class="ai-chat">
                            <div
                                v-for="(round, index) in rounds"
                                :key="index"
                                class="ai-chat__inner"
                            >
                                <div class="ai-chat__user">
                                    {{round.question}}
                                </div>

                                <div class="ai-chat__machine" :class="{'long-div': index === rounds.length - 1}">
                                    <div v-if="loaderMessage.length && index === rounds.length -1">
                                        <AiChatLoader :message="loaderMessage"/>
                                    </div>

                                    <p v-html="sanitizeAnswer(round.answer)"/>
                                </div>
                            </div>
                        </div>

                        <div v-if="!loading && !showResult" class="ai-chat__footer">
                            <div class="card--ai__search">
                        <span
                            role="textbox"
                            contenteditable
                            v-focus
                            placeholder="Ask about startups or partnerships"
                            class="card--ai__search__input"
                            @paste.prevent="stripPaste"
                            @input="question = $event.target.textContent"
                            @keyup.enter.prevent="sendRequest"
                        />

                                <button class="card--ai__search__button" @click="sendRequest"></button>
                            </div>
                        </div>
                    </template>
                </div>

                <div v-if="showResult">
                    <p>Here's a summary of your need. Review and edit these fields before creating it.</p>

                    <div class="mb-5">
                        <h5 class="heading5 heading5--secondary">Title</h5>
                        <input type="text" class="form-control form-control--light" v-model="need.name"/>
                    </div>

                    <div class="mb-5">
                        <h5 class="heading5 heading5--secondary">Description</h5>
                        <textarea class="form-control form-control--light form-control--lg" v-model="need.description"/>
                    </div>

                    <div class="pt-5 text-right">
                        <a class="btn btn-primary btn-lg" @click="createNeed">Create Need</a>
                    </div>
                </div>
            </div>
        </section>
        <footer class="ai-chat--need__footer">
            <div>
                <h5 class="heading5 heading5--secondary">
                    Visibility: AI + Network Partners 

                    <b-dropdown id="dropdown-1" text="Change" class="dropdown--need">
                        <div class="dropdown--need__inner">
                            <h6 class="heading6">Unlock recommendations from top venture and accelerator partners</h6>

                            <p>Your request is shared in a summarized, anonymized format with leading VCs, accelerators, and incubators. These partners surface startups from their portfolios and networks that match your need.</p>
                        </div>
                        <div class="dropdown--need__inner dropdown--need__inner--dark">
                            <h6 class="heading6">Visibility Settings</h6>

                            <div class="card-headline__content">
                                <div class="input-radio mb-3">
                                    <input type="radio" name="sinp" id="s1">
                                    <label for="s1">
                                        <div class="input-radio__inner">
                                            AI + Network Partners (Recommended)
                                            <p>Get insights from SwitchPitch AI and our venture ecosystem partners</p>
                                        </div>
                                    </label>
                                </div>
                                <div class="input-radio">
                                    <input type="radio" name="sinp" id="s2">
                                    <label for="s2">
                                        <div class="input-radio__inner">
                                            AI Only
                                            <p>Your request will not be shared with ecosystem partners</p>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </b-dropdown>
                </h5>

                <p class="modal-text">Get insights from AI and our venture ecosystem partners</p>
            </div>
        </footer>

        <ConfirmModal
            :show.sync="showConfirmModal"
            title="Discard Need?"
            body="This can’t be undone"
            :options="[
                {label: 'Cancel', value: 'cancel'},
                {label: 'Discard', value: 'discard', variant: 'danger'}
            ]"
            @clicked="closeConfirmed"
        />
    </b-modal>
</template>

<style scoped>
.long-div {
    min-height: 140px;
}

.section-ai-chat {
    overflow-y: auto;
    scroll-behavior: smooth;
    padding-bottom: 160px;
}

.ai-chat__footer {
    pointer-events: none;
}

.ai-chat__footer .card--ai__search {
    pointer-events: auto;
}
</style>
